import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EventIcon from '@mui/icons-material/Event';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LockIcon from '@mui/icons-material/Lock';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {
  assignMockInterviews,
  completeMockInterview,
  deleteMockInterview,
  fetchCohortMembers,
  fetchMockInterviewTemplates,
  fetchMockInterviews,
  notifyMockInterview,
  scoreMockInterview,
  updateMockInterview,
} from '../../api/cohortApi';

// Two audiences see this board very differently — see repositories.py's
// _mock_interview_to_dict for the server-side masking this UI mirrors:
// - Everyone in the cohort sees the public "job advert" (title, company,
//   description, topics, schedule) like a job board.
// - Only the assigned interviewer(s) and admins ever see `questions` and
//   `grading_rubric` before the interview happens — the candidate does not,
//   which is the whole point (an unbiased mock interview). The candidate's
//   own row only gains `questions`/`grading_rubric`/`interview_scores` once
//   `revealed` flips true, which happens when the cycle is marked complete.

const SCORE_OPTIONS = [
  [1, '1 — Needs work'],
  [2, '2 — Developing'],
  [3, '3 — Solid'],
  [4, '4 — Strong'],
];

const QUESTION_CATEGORIES = [
  { key: 'culture', label: 'Culture', helper: 'Motivation, values, role fit, self-awareness' },
  { key: 'behaviour', label: 'Behaviour', helper: 'STAR stories, teamwork, pace, decisions, pressure' },
  { key: 'technical', label: 'Technical', helper: 'Tools, concepts, analysis method, problem solving' },
];

const inferQuestionCategory = (question = {}) => {
  const raw = `${question.category || ''} ${question.focus_area || ''} ${question.question || ''}`.toLowerCase();
  if (raw.includes('culture') || raw.includes('fit') || raw.includes('motivation') || raw.includes('development') || raw.includes('self-awareness')) {
    return 'culture';
  }
  if (
    raw.includes('behaviour') || raw.includes('behavior') || raw.includes('star') || raw.includes('team') ||
    raw.includes('pace') || raw.includes('pressure') || raw.includes('stakeholder') || raw.includes('decision') ||
    raw.includes('challenge') || raw.includes('communicat')
  ) {
    return 'behaviour';
  }
  return 'technical';
};

const questionKeyFor = (question, index) => question.id || question.key || `q${index + 1}`;

const groupQuestionsByCategory = (questions = []) => QUESTION_CATEGORIES.map((category) => ({
  ...category,
  questions: questions
    .map((question, index) => ({ ...question, _index: index, _key: questionKeyFor(question, index), _category: inferQuestionCategory(question) }))
    .filter((question) => question._category === category.key),
})).filter((category) => category.questions.length > 0);

const STATUS_META = {
  scheduled: { label: 'Scheduled', color: 'info' },
  in_progress: { label: 'Live', color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
  no_show: { label: 'No-show', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

const formatSchedule = (scheduledAt) => {
  if (!scheduledAt) return 'Unscheduled';
  const date = new Date(scheduledAt);
  if (Number.isNaN(date.getTime())) return 'Unscheduled';
  return date.toLocaleString(undefined, {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

// Same local-time round-trip as ShowTellBoard's AssignSessionDialog.
const localInputToIso = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const isoToLocalInput = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const slugifyKey = (label, fallbackIndex) => (
  String(label || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  || `lens_${fallbackIndex}`
);

function AssignMockInterviewDialog({ open, onClose, members, templates, defaultCycleNumber, cohortKey, onAssigned }) {
  const [cycleNumber, setCycleNumber] = useState(defaultCycleNumber || 1);
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('InsiTech International');
  const [jobDescription, setJobDescription] = useState('');
  const [topicsCovered, setTopicsCovered] = useState([]);
  const [questions, setQuestions] = useState([{ question: '', category: 'technical', focus_area: '', strong_answer_notes: '' }]);
  const [gradingRubric, setGradingRubric] = useState([{ key: '', label: '', criteria: '' }]);
  const [rows, setRows] = useState([{ candidate: null, interviewers: [], scheduledAtLocal: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setCycleNumber(defaultCycleNumber || 1);
      setJobTitle('');
      setCompanyName('InsiTech International');
      setJobDescription('');
      setTopicsCovered([]);
      setQuestions([{ question: '', category: 'technical', focus_area: '', strong_answer_notes: '' }]);
      setGradingRubric([{ key: '', label: '', criteria: '' }]);
      setRows([{ candidate: null, interviewers: [], scheduledAtLocal: '' }]);
      setError('');
    }
  }, [open, defaultCycleNumber]);

  const applyTemplate = (template) => {
    if (!template) return;
    setJobTitle(template.job_title || '');
    setCompanyName(template.company_name || 'InsiTech International');
    setJobDescription(template.job_description || '');
    setTopicsCovered(template.topics_covered || []);
    setQuestions(
      (template.sample_questions || []).map((q) => ({
        question: q.question || '',
        category: q.category || inferQuestionCategory(q),
        focus_area: q.focus_area || '',
        strong_answer_notes: q.strong_answer_notes || '',
      }))
    );
    setGradingRubric(
      (template.rubric_lenses || []).map((lens) => ({
        key: lens.key || '',
        label: lens.label || '',
        criteria: lens.criteria || '',
      }))
    );
  };

  const updateQuestion = (index, patch) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  };
  const addQuestion = () => setQuestions((prev) => [...prev, { question: '', category: 'technical', focus_area: '', strong_answer_notes: '' }]);
  const removeQuestion = (index) => setQuestions((prev) => prev.filter((_, i) => i !== index));

  const updateRubricLens = (index, patch) => {
    setGradingRubric((prev) => prev.map((lens, i) => (i === index ? { ...lens, ...patch } : lens)));
  };
  const addRubricLens = () => setGradingRubric((prev) => [...prev, { key: '', label: '', criteria: '' }]);
  const removeRubricLens = (index) => setGradingRubric((prev) => prev.filter((_, i) => i !== index));

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const addRow = () => setRows((prev) => [...prev, { candidate: null, interviewers: [], scheduledAtLocal: '' }]);
  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (!jobTitle.trim()) {
      setError('Give the job posting a title.');
      return;
    }
    const cleanQuestions = questions
      .filter((q) => q.question.trim())
      .map((q) => ({
        question: q.question.trim(),
        category: q.category || inferQuestionCategory(q),
        focus_area: (q.focus_area || '').trim(),
        strong_answer_notes: (q.strong_answer_notes || '').trim(),
      }));
    if (!cleanQuestions.length) {
      setError('Add at least one interview question.');
      return;
    }
    const cleanRubric = gradingRubric
      .filter((lens) => lens.label.trim())
      .map((lens, index) => ({
        key: lens.key.trim() || slugifyKey(lens.label, index),
        label: lens.label.trim(),
        criteria: (lens.criteria || '').trim(),
      }));
    const cleanRows = rows.filter((row) => row.candidate && row.interviewers.length && row.scheduledAtLocal);
    if (!cleanRows.length) {
      setError('Add at least one candidate with an interviewer and a scheduled time.');
      return;
    }
    for (const row of cleanRows) {
      if (row.interviewers.some((i) => i.id === row.candidate.id)) {
        setError(`${row.candidate.label} cannot also be their own interviewer.`);
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const assigned = await assignMockInterviews(cohortKey, {
        cycle_number: Number(cycleNumber) || 1,
        job_title: jobTitle.trim(),
        company_name: companyName.trim() || 'InsiTech International',
        job_description: jobDescription.trim(),
        topics_covered: topicsCovered,
        questions: cleanQuestions,
        grading_rubric: cleanRubric,
        assignments: cleanRows.map((row) => ({
          candidate_user_id: row.candidate.id,
          interviewer_user_ids: row.interviewers.map((i) => i.id),
          scheduled_at: localInputToIso(row.scheduledAtLocal),
        })),
      });
      onAssigned(assigned || []);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not post this mock interview cycle.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Post a job & assign a mock interview</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Autocomplete
            options={templates}
            getOptionLabel={(t) => [t.job_title, t.company_name].filter(Boolean).join(' \u2014 ')}
            onChange={(_, value) => applyTemplate(value)}
            size="small"
            renderInput={(params) => <TextField {...params} label="Start from a job-advert template (optional)" />}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Cycle number"
              type="number"
              value={cycleNumber}
              onChange={(event) => setCycleNumber(event.target.value)}
              size="small"
              sx={{ width: 160 }}
              helperText="Bumps every 2 weeks"
            />
            <TextField
              label="Job title"
              value={jobTitle}
              onChange={(event) => setJobTitle(event.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Company"
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value)}
              size="small"
              sx={{ width: 240 }}
            />
          </Stack>

          <TextField
            label="Job description (visible to the whole cohort)"
            value={jobDescription}
            onChange={(event) => setJobDescription(event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={topicsCovered}
            onChange={(_, value) => setTopicsCovered(value)}
            size="small"
            renderInput={(params) => <TextField {...params} label="Topics covered by this role" placeholder="Type a topic and press enter" />}
          />

          <Divider />
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>
              Interview questions <LockIcon fontSize="inherit" sx={{ verticalAlign: 'middle', ml: 0.5 }} />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Hidden from the candidate until the interview is marked complete — only the interviewer(s) and admins see these.
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {questions.map((q, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      <TextField
                        label="Question"
                        value={q.question}
                        onChange={(event) => updateQuestion(index, { question: event.target.value })}
                        multiline
                        size="small"
                        fullWidth
                      />
                      <Stack direction="row" spacing={1}>
                        <TextField
                          select
                          label="Strategy"
                          value={q.category || inferQuestionCategory(q)}
                          onChange={(event) => updateQuestion(index, { category: event.target.value })}
                          size="small"
                          sx={{ width: 180 }}
                        >
                          {QUESTION_CATEGORIES.map((category) => (
                            <MenuItem key={category.key} value={category.key}>{category.label}</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="Focus area"
                          value={q.focus_area}
                          onChange={(event) => updateQuestion(index, { focus_area: event.target.value })}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                      </Stack>
                      <TextField
                        label="What a strong answer looks like (interviewer's private cheat-sheet)"
                        value={q.strong_answer_notes}
                        onChange={(event) => updateQuestion(index, { strong_answer_notes: event.target.value })}
                        multiline
                        size="small"
                        fullWidth
                      />
                    </Stack>
                    <IconButton size="small" onClick={() => removeQuestion(index)} disabled={questions.length === 1}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>
            <Button startIcon={<AddIcon />} onClick={addQuestion} size="small" sx={{ mt: 1 }}>
              Add question
            </Button>
          </Box>

          <Divider />
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>Grading scheme</Typography>
            <Typography variant="caption" color="text.secondary">
              Each lens is scored 1-4 by the interviewer during/after the session.
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {gradingRubric.map((lens, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label="Lens name"
                    value={lens.label}
                    onChange={(event) => updateRubricLens(index, { label: event.target.value })}
                    size="small"
                    sx={{ width: 220 }}
                  />
                  <TextField
                    label="What it's judging"
                    value={lens.criteria}
                    onChange={(event) => updateRubricLens(index, { criteria: event.target.value })}
                    size="small"
                    fullWidth
                  />
                  <IconButton size="small" onClick={() => removeRubricLens(index)} disabled={gradingRubric.length === 1}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button startIcon={<AddIcon />} onClick={addRubricLens} size="small" sx={{ mt: 1 }}>
              Add grading lens
            </Button>
          </Box>

          <Divider />
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>Assign candidate(s) & interviewer(s)</Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {rows.map((row, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <Autocomplete
                    options={members}
                    getOptionLabel={(option) => option.label || option.email || ''}
                    value={row.candidate}
                    onChange={(_, value) => updateRow(index, { candidate: value })}
                    size="small"
                    sx={{ flex: 1 }}
                    renderInput={(params) => <TextField {...params} label="Candidate" />}
                  />
                  <Autocomplete
                    multiple
                    options={members}
                    getOptionLabel={(option) => option.label || option.email || ''}
                    value={row.interviewers}
                    onChange={(_, value) => updateRow(index, { interviewers: value })}
                    size="small"
                    sx={{ flex: 1.4 }}
                    renderInput={(params) => <TextField {...params} label="Interviewer(s)" />}
                  />
                  <TextField
                    label="Scheduled"
                    type="datetime-local"
                    value={row.scheduledAtLocal}
                    onChange={(event) => updateRow(index, { scheduledAtLocal: event.target.value })}
                    size="small"
                    sx={{ width: 220 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <IconButton size="small" onClick={() => removeRow(index)} disabled={rows.length === 1}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button startIcon={<AddIcon />} onClick={addRow} size="small" sx={{ mt: 1 }}>
              Add candidate
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Posting…' : 'Post & assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function InterviewerPanelDialog({ cycle, currentUserId, isAdmin, onClose, onUpdated, cohortKey }) {
  const isInterviewer = (cycle.interviewer_user_ids || []).includes(currentUserId);
  const myExistingScore = (cycle.interview_scores || {})[currentUserId];
  const [scores, setScores] = useState(() => ({ ...(myExistingScore?.rubric_scores || {}) }));
  const [questionScores, setQuestionScores] = useState(() => ({ ...(myExistingScore?.question_scores || {}) }));
  const [feedbackNote, setFeedbackNote] = useState(myExistingScore?.feedback_note || '');
  const [feedbackSummary, setFeedbackSummary] = useState(cycle.feedback_summary || '');
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');

  const rubric = cycle.grading_rubric || [];
  const interviewerNames = cycle.interviewer_names || [];
  const interviewerIds = cycle.interviewer_user_ids || [];
  const scoresById = cycle.interview_scores || {};
  const questionGroups = groupQuestionsByCategory(cycle.questions || []);

  const updateQuestionScore = (questionKey, lensKey, value) => {
    setQuestionScores((prev) => ({
      ...prev,
      [questionKey]: {
        ...(prev[questionKey] || {}),
        rubric_scores: {
          ...((prev[questionKey] || {}).rubric_scores || {}),
          [lensKey]: Number(value),
        },
      },
    }));
  };

  const updateQuestionFeedback = (questionKey, feedback) => {
    setQuestionScores((prev) => ({
      ...prev,
      [questionKey]: {
        ...(prev[questionKey] || {}),
        feedback_note: feedback,
      },
    }));
  };

  const handleSaveScore = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await scoreMockInterview(cohortKey, cycle.id, {
        rubric_scores: scores,
        question_scores: questionScores,
        feedback_note: feedbackNote,
      });
      onUpdated(result);
    } catch (err) {
      setError(err.message || 'Could not save your score.');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (nextStatus) => {
    setCompleting(true);
    setError('');
    try {
      const result = await completeMockInterview(cohortKey, cycle.id, {
        status: nextStatus,
        feedback_summary: feedbackSummary,
      });
      onUpdated(result);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not update this interview.');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {cycle.job_title} — {cycle.candidate_name || 'Candidate'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info">
            Confidential interview material — stays hidden from the candidate until this interview is marked complete.
          </Alert>

          <Box>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>Questions to ask</Typography>
            <Stack spacing={2}>
              {questionGroups.map((group) => (
                <Box key={group.key}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Chip size="small" label={group.label} color={group.key === 'technical' ? 'primary' : 'default'} />
                    <Typography variant="caption" color="text.secondary">{group.helper}</Typography>
                  </Stack>
                  <Stack spacing={1.5}>
                    {group.questions.map((q) => (
                      <Paper key={q._key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2" fontWeight={700}>{q._index + 1}. {q.question}</Typography>
                          {interviewerNames.length > 0 && (
                            <Chip
                              size="small"
                              color={interviewerIds[q._index % interviewerIds.length] === currentUserId ? 'primary' : 'default'}
                              label={`Suggested asker: ${interviewerNames[q._index % interviewerNames.length] || 'Interviewer'}`}
                              variant="outlined"
                            />
                          )}
                          {q.focus_area && <Chip size="small" label={q.focus_area} variant="outlined" />}
                        </Stack>
                        {q.strong_answer_notes && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Strong answer looks like: {q.strong_answer_notes}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>

          <Divider />

          {isInterviewer ? (
            <Box>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Your grading — score each answer</Typography>
              <Stack spacing={2}>
                {questionGroups.map((group) => (
                  <Box key={group.key}>
                    <Chip size="small" label={group.label} sx={{ mb: 1 }} />
                    <Stack spacing={1.5}>
                      {group.questions.map((q) => {
                        const entry = questionScores[q._key] || {};
                        return (
                          <Paper key={q._key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="body2" fontWeight={800} gutterBottom>{q._index + 1}. {q.question}</Typography>
                            <Grid container spacing={1.5}>
                              {rubric.map((lens) => (
                                <Grid item xs={12} sm={6} key={lens.key}>
                                  <TextField
                                    select
                                    label={lens.label}
                                    helperText={lens.criteria}
                                    value={(entry.rubric_scores || {})[lens.key] || ''}
                                    onChange={(event) => updateQuestionScore(q._key, lens.key, event.target.value)}
                                    size="small"
                                    fullWidth
                                  >
                                    {SCORE_OPTIONS.map(([value, label]) => (
                                      <MenuItem key={value} value={value}>{label}</MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                              ))}
                            </Grid>
                            <TextField
                              label="Feedback for this answer"
                              value={entry.feedback_note || ''}
                              onChange={(event) => updateQuestionFeedback(q._key, event.target.value)}
                              multiline
                              minRows={2}
                              fullWidth
                              sx={{ mt: 1.5 }}
                            />
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mt: 2 }} gutterBottom>Overall scorecard</Typography>
              <Grid container spacing={1.5}>
                {rubric.map((lens) => (
                  <Grid item xs={12} sm={6} key={lens.key}>
                    <TextField
                      select
                      label={lens.label}
                      helperText="Optional final overall score across the interview."
                      value={scores[lens.key] || ''}
                      onChange={(event) => setScores((prev) => ({ ...prev, [lens.key]: Number(event.target.value) }))}
                      size="small"
                      fullWidth
                    >
                      {SCORE_OPTIONS.map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                ))}
              </Grid>
              <TextField
                label="Overall feedback for the candidate (shown after reveal)"
                value={feedbackNote}
                onChange={(event) => setFeedbackNote(event.target.value)}
                multiline
                minRows={2}
                fullWidth
                sx={{ mt: 1.5 }}
              />
              <Button variant="outlined" onClick={handleSaveScore} disabled={saving} sx={{ mt: 1 }}>
                {saving ? 'Saving…' : myExistingScore ? 'Update my score' : 'Save my score'}
              </Button>
            </Box>
          ) : (
            <Alert severity="info">You're viewing this as an admin — only assigned interviewers submit scores.</Alert>
          )}

          {interviewerIds.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Panel scores so far</Typography>
              <Stack spacing={1}>
                {interviewerIds.map((id, index) => {
                  const entry = scoresById[id];
                  return (
                    <Paper key={id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="body2" fontWeight={700}>{interviewerNames[index] || 'Interviewer'}</Typography>
                      {entry ? (
                        <>
                          {Object.keys(entry.question_scores || {}).length > 0 && (
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              {questionGroups.map((group) => (
                                <Box key={group.key}>
                                  <Typography variant="caption" fontWeight={800}>{group.label}</Typography>
                                  {group.questions.map((q) => {
                                    const qEntry = (entry.question_scores || {})[q._key];
                                    if (!qEntry) return null;
                                    return (
                                      <Box key={q._key} sx={{ mt: 0.5 }}>
                                        <Typography variant="caption" display="block">{q._index + 1}. {q.question}</Typography>
                                        <Stack direction="row" spacing={2} flexWrap="wrap">
                                          {Object.entries(qEntry.rubric_scores || {}).map(([key, value]) => {
                                            const lens = rubric.find((l) => l.key === key);
                                            return <Typography key={key} variant="caption">{lens?.label || key}: <b>{value}/4</b></Typography>;
                                          })}
                                        </Stack>
                                        {qEntry.feedback_note && (
                                          <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>"{qEntry.feedback_note}"</Typography>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ))}
                            </Stack>
                          )}
                          {Object.keys(entry.rubric_scores || {}).length > 0 && (
                            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {Object.entries(entry.rubric_scores || {}).map(([key, value]) => {
                                const lens = rubric.find((l) => l.key === key);
                                return <Typography key={key} variant="caption">Overall {lens?.label || key}: <b>{value}/4</b></Typography>;
                              })}
                            </Stack>
                          )}
                          {entry.feedback_note && (
                            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>"{entry.feedback_note}"</Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Not submitted yet.</Typography>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          )}

          <Divider />
          {isAdmin ? (
            <Box>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Wrap up the interview</Typography>
              <Typography variant="caption" color="text.secondary">
                Panel scores are kept separately. Review any differences before revealing the questions, grading scheme,
                and all scores to the candidate.
              </Typography>
              <TextField
                label="Overall feedback summary for the candidate"
                value={feedbackSummary}
                onChange={(event) => setFeedbackSummary(event.target.value)}
                multiline
                minRows={2}
                fullWidth
                sx={{ mt: 1 }}
              />
            </Box>
          ) : (
            <Alert severity="info">
              Save your score and feedback here. An admin finalises the interview after reviewing the panel scores.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {isAdmin && (
          <>
            <Button color="warning" onClick={() => handleComplete('cancelled')} disabled={completing}>Cancel cycle</Button>
            <Button color="error" onClick={() => handleComplete('no_show')} disabled={completing}>Mark no-show</Button>
            <Button variant="contained" onClick={() => handleComplete('completed')} disabled={completing}>
              {completing ? 'Saving…' : 'Mark complete & reveal'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function CandidateFeedbackDialog({ cycle, onClose }) {
  const rubric = cycle.grading_rubric || [];
  const interviewerNames = cycle.interviewer_names || [];
  const interviewerIds = cycle.interviewer_user_ids || [];
  const scoresById = cycle.interview_scores || {};
  const questionGroups = groupQuestionsByCategory(cycle.questions || []);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{cycle.job_title} — your interview feedback</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {cycle.feedback_summary && <Alert severity="success">{cycle.feedback_summary}</Alert>}

          <Box>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>What you were asked</Typography>
            <Stack spacing={2}>
              {questionGroups.map((group) => (
                <Box key={group.key}>
                  <Chip size="small" label={group.label} sx={{ mb: 1 }} />
                  <Stack spacing={1.5}>
                    {group.questions.map((q) => (
                      <Paper key={q._key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Typography variant="body2" fontWeight={700}>{q._index + 1}. {q.question}</Typography>
                        {q.focus_area && <Chip size="small" label={q.focus_area} variant="outlined" sx={{ mt: 0.5 }} />}
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>How you were graded</Typography>
            <Stack spacing={1}>
              {interviewerIds.map((id, index) => {
                const entry = scoresById[id];
                if (!entry) return null;
                return (
                  <Paper key={id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight={700}>{interviewerNames[index] || 'Interviewer'}</Typography>
                    {Object.keys(entry.question_scores || {}).length > 0 && (
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {questionGroups.map((group) => (
                          <Box key={group.key}>
                            <Typography variant="caption" fontWeight={800}>{group.label}</Typography>
                            {group.questions.map((q) => {
                              const qEntry = (entry.question_scores || {})[q._key];
                              if (!qEntry) return null;
                              return (
                                <Box key={q._key} sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" display="block">{q._index + 1}. {q.question}</Typography>
                                  <Stack direction="row" spacing={2} flexWrap="wrap">
                                    {Object.entries(qEntry.rubric_scores || {}).map(([key, value]) => {
                                      const lens = rubric.find((l) => l.key === key);
                                      return <Typography key={key} variant="caption">{lens?.label || key}: <b>{value}/4</b></Typography>;
                                    })}
                                  </Stack>
                                  {qEntry.feedback_note && (
                                    <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>"{qEntry.feedback_note}"</Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        ))}
                      </Stack>
                    )}
                    {Object.keys(entry.rubric_scores || {}).length > 0 && (
                      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {Object.entries(entry.rubric_scores || {}).map(([key, value]) => {
                          const lens = rubric.find((l) => l.key === key);
                          return <Typography key={key} variant="caption">Overall {lens?.label || key}: <b>{value}/4</b></Typography>;
                        })}
                      </Stack>
                    )}
                    {entry.feedback_note && (
                      <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>"{entry.feedback_note}"</Typography>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function MockInterviewBoard({ cohortKey, isAdmin, currentUserId, defaultCycleNumber = 1 }) {
  const [cycles, setCycles] = useState([]);
  const [members, setMembers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [panelTarget, setPanelTarget] = useState(null);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [notifyingCycleId, setNotifyingCycleId] = useState(null);
  const [updatingCycleId, setUpdatingCycleId] = useState(null);

  const reload = () => {
    setLoading(true);
    Promise.all([
      fetchMockInterviews(cohortKey).catch(() => []),
      isAdmin ? fetchCohortMembers(cohortKey).catch(() => []) : Promise.resolve([]),
      fetchMockInterviewTemplates(cohortKey).catch(() => []),
    ])
      .then(([cycleRows, memberRows, templateRows]) => {
        setCycles(cycleRows || []);
        setMembers(memberRows || []);
        setTemplates(templateRows || []);
        setError('');
      })
      .catch((err) => setError(err.message || 'Could not load mock interviews.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (cohortKey) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortKey, isAdmin]);

  const jobGroups = useMemo(() => {
    const groups = new Map();
    cycles.forEach((cycle) => {
      const key = `${cycle.cycle_number}::${cycle.job_title}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          cycle_number: cycle.cycle_number,
          job_title: cycle.job_title,
          company_name: cycle.company_name,
          job_description: cycle.job_description,
          topics_covered: cycle.topics_covered || [],
          rows: [],
        });
      }
      groups.get(key).rows.push(cycle);
    });
    return Array.from(groups.values()).sort((a, b) => b.cycle_number - a.cycle_number);
  }, [cycles]);

  const myRows = useMemo(
    () => cycles
      .filter((cycle) => cycle.viewer_role === 'candidate' || cycle.viewer_role === 'interviewer')
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)),
    [cycles]
  );

  const patchCycle = (updated) => {
    setCycles((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
  };

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const notificationMessage = (totals, successPrefix = 'Mock interview assigned.') => {
    if (totals.sent > 0 && totals.failed === 0) {
      return `${successPrefix} Notifications sent to ${totals.sent} candidate/interviewer recipient${totals.sent === 1 ? '' : 's'}.`;
    }
    if (totals.failed > 0) {
      return `${successPrefix} ${totals.failed} of ${totals.attempted} notification email${totals.attempted === 1 ? '' : 's'} could not be sent.`;
    }
    if (totals.skipped > 0) {
      return `${successPrefix} One or more recipients had no email address.`;
    }
    return `${successPrefix} No notification emails were sent; check email configuration.`;
  };

  const handleAssigned = (assignedRows = []) => {
    const totals = assignedRows.reduce(
      (acc, row) => {
        const result = row._notification_result || {};
        acc.attempted += result.attempted || 0;
        acc.sent += result.sent || 0;
        acc.failed += result.failed || 0;
        acc.skipped += result.skipped || 0;
        return acc;
      },
      { attempted: 0, sent: 0, failed: 0, skipped: 0 }
    );
    const severity = totals.failed > 0 || totals.skipped > 0 || totals.sent === 0 ? 'warning' : 'success';
    showToast(notificationMessage(totals), severity);
    reload();
  };

  const handleNotify = async (cycle) => {
    setNotifyingCycleId(cycle.id);
    try {
      const result = await notifyMockInterview(cohortKey, cycle.id);
      const totals = result || {};
      const severity = totals.failed > 0 || totals.skipped > 0 || totals.sent === 0 ? 'warning' : 'success';
      showToast(notificationMessage(totals, 'Mock interview notification sent.'), severity);
    } catch (err) {
      showToast(err.message || 'Could not send mock interview notifications.', 'error');
    } finally {
      setNotifyingCycleId(null);
    }
  };

  const handleStatusUpdate = async (cycle, nextStatus) => {
    setUpdatingCycleId(cycle.id);
    try {
      const result = await updateMockInterview(cohortKey, cycle.id, { status: nextStatus });
      patchCycle(result);
      showToast(`Mock interview marked ${STATUS_META[nextStatus]?.label || nextStatus}.`, 'success');
    } catch (err) {
      showToast(err.message || 'Could not update this mock interview.', 'error');
    } finally {
      setUpdatingCycleId(null);
    }
  };

  const handleReschedule = async (cycle) => {
    const currentValue = isoToLocalInput(cycle.scheduled_at);
    const nextValue = window.prompt('New scheduled time (YYYY-MM-DDTHH:mm)', currentValue);
    if (!nextValue) return;
    const scheduledAt = localInputToIso(nextValue);
    if (!scheduledAt) {
      showToast('Use a valid date and time for rescheduling.', 'warning');
      return;
    }
    setUpdatingCycleId(cycle.id);
    try {
      const result = await updateMockInterview(cohortKey, cycle.id, {
        status: 'scheduled',
        scheduled_at: scheduledAt,
      });
      patchCycle(result);
      showToast('Mock interview rescheduled. Use Notify to send the updated brief.', 'success');
    } catch (err) {
      showToast(err.message || 'Could not reschedule this mock interview.', 'error');
    } finally {
      setUpdatingCycleId(null);
    }
  };

  const handleDelete = async (cycle) => {
    const ok = window.confirm(`Delete the mock interview for ${cycle.candidate_name || 'this candidate'}? This removes it from the cohort board.`);
    if (!ok) return;
    setUpdatingCycleId(cycle.id);
    try {
      await deleteMockInterview(cohortKey, cycle.id);
      setCycles((prev) => prev.filter((row) => row.id !== cycle.id));
      showToast('Mock interview deleted.', 'success');
    } catch (err) {
      showToast(err.message || 'Could not delete this mock interview.', 'error');
    } finally {
      setUpdatingCycleId(null);
    }
  };

  if (!cohortKey) return null;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isAdmin && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1.5}>
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>Mock interview cycles</Typography>
              <Typography variant="body2" color="text.secondary">
                Post a fake InsiTech job advert every 2 weeks, assign a candidate and interviewer(s). Questions and
                grading stay private to the interviewer until the session is marked complete.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAssignOpen(true)}>
              Post job & assign
            </Button>
          </Stack>
        </Paper>
      )}

      {!isAdmin && myRows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} gutterBottom>Your Mock Interviews</Typography>
          <Stack spacing={1.5}>
            {myRows.map((cycle) => {
              const meta = STATUS_META[cycle.status] || STATUS_META.scheduled;
              return (
                <Paper key={cycle.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" rowGap={1}>
                    <Box>
                      <Typography variant="body1" fontWeight={700}>{cycle.job_title}</Typography>
                      <Typography variant="caption" color="text.secondary">{cycle.company_name}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">{formatSchedule(cycle.scheduled_at)}</Typography>
                        <Chip size="small" label={meta.label} color={meta.color} />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={cycle.viewer_role === 'candidate' ? "You're the candidate" : "You're interviewing"}
                        />
                      </Stack>
                    </Box>
                    {cycle.viewer_role === 'interviewer' && (
                      <Button size="small" variant="outlined" onClick={() => setPanelTarget(cycle)}>
                        {cycle.status === 'scheduled' ? 'Prep & conduct' : 'View / edit score'}
                      </Button>
                    )}
                    {cycle.viewer_role === 'candidate' && (
                      cycle.revealed ? (
                        <Button size="small" variant="outlined" onClick={() => setFeedbackTarget(cycle)}>
                          View feedback
                        </Button>
                      ) : (
                        <Chip size="small" icon={<LockIcon sx={{ fontSize: 14 }} />} label="Questions hidden until after your interview" />
                      )
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      )}

      {!loading && jobGroups.length === 0 && (
        <Alert severity="info">No mock interview cycles posted yet.</Alert>
      )}

      {jobGroups.map((group) => (
        <Paper key={group.key} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <WorkOutlineIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={800}>{group.job_title}</Typography>
            <Chip size="small" label={`Cycle ${group.cycle_number}`} variant="outlined" />
            <Typography variant="caption" color="text.secondary">{group.company_name}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 780, mb: 1 }}>
            {group.job_description}
          </Typography>
          {group.topics_covered.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5} sx={{ mb: 1.5 }}>
              {group.topics_covered.map((topic) => (
                <Chip key={topic} size="small" label={topic} />
              ))}
            </Stack>
          )}
          <Divider sx={{ mb: 1.5 }} />

          {isAdmin ? (
            <Stack spacing={1}>
              {group.rows.map((cycle, index) => {
                const meta = STATUS_META[cycle.status] || STATUS_META.scheduled;
                return (
                  <Stack
                    key={cycle.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    rowGap={1}
                    sx={{ py: 0.75, px: 1, borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Box>
                      <Typography variant="body2">
                        <b>{index + 1}.</b> {cycle.candidate_name} — interviewed by {(cycle.interviewer_names || []).join(', ') || '—'}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">{formatSchedule(cycle.scheduled_at)}</Typography>
                        <Chip size="small" label={meta.label} color={meta.color} />
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={0.75} justifyContent="flex-end">
                      {cycle.status === 'scheduled' && (
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          disabled={updatingCycleId === cycle.id}
                          onClick={() => handleStatusUpdate(cycle, 'in_progress')}
                        >
                          Make live
                        </Button>
                      )}
                      {cycle.status === 'in_progress' && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={updatingCycleId === cycle.id}
                          onClick={() => handleStatusUpdate(cycle, 'scheduled')}
                        >
                          Back to scheduled
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={updatingCycleId === cycle.id}
                        onClick={() => handleReschedule(cycle)}
                      >
                        Reschedule
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={notifyingCycleId === cycle.id || updatingCycleId === cycle.id}
                        onClick={() => handleNotify(cycle)}
                      >
                        {notifyingCycleId === cycle.id ? 'Sending...' : 'Notify'}
                      </Button>
                      {cycle.status !== 'cancelled' && (
                        <Button
                          size="small"
                          color="warning"
                          disabled={updatingCycleId === cycle.id}
                          onClick={() => handleStatusUpdate(cycle, 'cancelled')}
                        >
                          Cancel
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="error"
                        disabled={updatingCycleId === cycle.id}
                        onClick={() => handleDelete(cycle)}
                      >
                        Delete
                      </Button>
                      <Button size="small" onClick={() => setPanelTarget(cycle)}>Manage</Button>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <PeopleAltIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {group.rows.length} cohort member{group.rows.length === 1 ? '' : 's'} interviewing this cycle
              </Typography>
            </Stack>
          )}
        </Paper>
      ))}

      <AssignMockInterviewDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        members={members}
        templates={templates}
        defaultCycleNumber={defaultCycleNumber}
        cohortKey={cohortKey}
        onAssigned={handleAssigned}
      />
      {panelTarget && (
        <InterviewerPanelDialog
          cycle={panelTarget}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          onClose={() => setPanelTarget(null)}
          onUpdated={(updated) => { patchCycle(updated); setPanelTarget(updated); }}
          cohortKey={cohortKey}
        />
      )}
      {feedbackTarget && (
        <CandidateFeedbackDialog cycle={feedbackTarget} onClose={() => setFeedbackTarget(null)} />
      )}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
